import {IAdvancedFilterCopyFailure, IAdvancedFilterCopyResult} from '../models/general/advanced-filter-copy-result.interface';

/**
 * Removes `undefined` values so Firestore `setDoc` does not reject the payload.
 */
export function stripUndefinedForFirestore<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry: unknown): unknown => stripUndefinedForFirestore(entry)) as T;
  }
  if (typeof value === 'object') {
    const output: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entry]: [string, unknown]): void => {
      if (entry !== undefined) {
        output[key] = stripUndefinedForFirestore(entry);
      }
    });
    return output as T;
  }
  return value;
}

/**
 * User-facing message for a filter copy write failure.
 */
export function formatAdvancedFilterCopyErrorMessage(error: unknown): string {
  if (error !== null && typeof error === 'object') {
    const record = error as { code?: string; message?: string };
    if (record.code === 'permission-denied') {
      return 'You do not have permission to copy filters to this store.';
    }
    if (typeof record.message === 'string' && record.message.length > 0) {
      return record.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
}

/**
 * Summary lines for copy result modals (success, partial failure, or all failed).
 */
export function buildAdvancedFilterCopyResultLines(
  result: IAdvancedFilterCopyResult,
  options: {
    copiedLine(count: number): string;
    failureHeader(count: number): string;
    skippedLine(count: number): string;
    maxFailureDetails?: number;
  },
): string[] {
  const lines: string[] = [];
  const maxDetails: number = options.maxFailureDetails ?? 5;

  if (result.copiedCount > 0) {
    lines.push(options.copiedLine(result.copiedCount));
  }
  if (result.skippedCount > 0) {
    lines.push(options.skippedLine(result.skippedCount));
  }
  if (result.failures.length > 0) {
    lines.push(options.failureHeader(result.failures.length));
    result.failures.slice(0, maxDetails).forEach((failure: IAdvancedFilterCopyFailure): void => {
      lines.push(`"${failure.filterName}": ${failure.message}`);
    });
    if (result.failures.length > maxDetails) {
      lines.push(`…and ${result.failures.length - maxDetails} more.`);
    }
  }
  return lines;
}
